
import { ReactNode } from "react";
import { useHasPermission } from "@/hooks/rbac/useUserPermissions";
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldAlert, LockIcon, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface SecurePageAccessProps {
  children: ReactNode;
  resourceName: string;
  requiredPermission?: "view" | "edit";
  title?: string;
  description?: string;
}

export default function SecurePageAccess({
  children,
  resourceName,
  requiredPermission = "view",
  title = "Access Restricted",
  description = "You don't have permission to access this page."
}: SecurePageAccessProps) {
  const { hasPermission, isLoading } = useHasPermission(resourceName, requiredPermission);
  const navigate = useNavigate();
  
  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center py-12">
        <GlassCard variant="strong" className="max-w-md mx-auto">
          <GlassCardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
            <Skeleton className="h-6 w-32 mx-auto mb-2" />
            <Skeleton className="h-4 w-48 mx-auto" />
          </GlassCardHeader>
          <GlassCardContent>
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-3/4 mx-auto mb-4" />
            <Skeleton className="h-10 w-48 mx-auto" />
          </GlassCardContent>
        </GlassCard>
      </div>
    );
  }
  
  if (hasPermission) {
    return <>{children}</>;
  }
  
  return (
    <div className="w-full flex items-center justify-center py-12">
        <GlassCard variant="strong" className="max-w-md mx-auto">
          <GlassCardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full bg-amber-100 text-amber-600">
              <ShieldAlert size={28} />
            </div>
          </div>
          <GlassCardTitle className="text-xl">{title}</GlassCardTitle>
          <GlassCardDescription>{description}</GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent>
          <p className="mb-4 text-sm text-muted-foreground text-center">
            You need {requiredPermission} permissions for <strong>{resourceName}</strong> to access this content.
          </p>
          <div className="flex justify-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate("/")}
              className="mt-2"
            >
              <LockIcon className="mr-2 h-4 w-4" />
              Return to Dashboard
            </Button>
          </div>
        </GlassCardContent>
      </GlassCard>
    </div>
  );
}
